import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Divider,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Send as SendIcon,
  OpenInNew as OpenInNewIcon,
  AttachFile as AttachFileIcon,
  WarningAmber as WarningAmberIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import DOMPurify from "dompurify";
import { useOutlookPlugin } from "../../hooks/useOutlookPlugin";
import type { AutomationAction } from "../../api/types";
import type {
  EmailMessage,
  EmailAttachment,
} from "@services/documents/email/types";
import { EmailPreviewAttachmentPicker } from "./EmailPreviewAttachmentPicker";

type EmailPreviewProps = {
  onDismiss: () => void;
} & (
  | {
      mode: "draft";
      action: AutomationAction;
      draftMessageId: string;
      draftTo?: string[];
      draftCc?: string[];
      draftSubject?: string;
      draftAttachments?: { attachmentId?: string; filename: string; contentType?: string; downloadUrl: string }[];
      missingAttachments?: { name: string }[];
      shipmentId?: string;
      onAddAttachment?: (attachmentId: string) => Promise<void>;
      onRemoveAttachment?: (attachmentId: string) => Promise<void>;
      replyToEmailId?: string;
      onSend: () => Promise<void>;
    }
  | {
      mode: "readonly";
      emailId: string;
    }
);

interface CheckInboxResponse {
  inInbox: boolean;
  messageId?: string;
  inboxEmail?: string;
}

const handleDownloadAttachment = (att: EmailAttachment & { downloadUrl?: string }) => {
  if (att.downloadUrl) {
    window.open(att.downloadUrl, "_blank");
    return;
  }
  if (!att.content) return;
  const byteCharacters = atob(att.content);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], {
    type: att.contentType,
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = att.filename || "attachment";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const RecipientLine: React.FC<{ label: string; emails: string[] }> = ({ label, emails }) => {
  if (emails.length === 0) return null;
  const first = emails[0];
  const rest = emails.length - 1;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
      {label}: {first}
      {rest > 0 && (
        <Tooltip title={emails.join(", ")} arrow>
          <span style={{ cursor: "pointer" }}>{` (+${rest} other${rest > 1 ? "s" : ""})`}</span>
        </Tooltip>
      )}
    </Typography>
  );
};

export const EmailPreview: React.FC<EmailPreviewProps> = (props) => {
  const { actions, api, apiClient } = useOutlookPlugin();
  const { onDismiss } = props;

  const [sending, setSending] = useState(false);
  const [prevEmail, setPrevEmail] = useState<EmailMessage | null>(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [removingAttachment, setRemovingAttachment] = useState<string | null>(null);
  const [missingDismissed, setMissingDismissed] = useState(false);

  const replyToEmailId = props.mode === "draft" ? props.replyToEmailId : undefined;
  const missingAttachments = props.mode === "draft" ? props.missingAttachments : undefined;
  const hasMissing = missingAttachments && missingAttachments.length > 0;
  const showMissing = hasMissing && !missingDismissed;
  const shipmentId = props.mode === "draft" ? props.shipmentId : undefined;
  const onAddAttachment = props.mode === "draft" ? props.onAddAttachment : undefined;
  const onRemoveAttachment = props.mode === "draft" ? props.onRemoveAttachment : undefined;

  const handleLoadPrevious = async () => {
    if (!replyToEmailId) return;
    setLoadingPrev(true);
    try {
      const data = await api.getEmailWithAttachments(replyToEmailId);
      setPrevEmail(data);
    } catch {
      // silently fail
    } finally {
      setLoadingPrev(false);
    }
  };

  // Outlook takes a few seconds to sync the draft — disable "Edit in Outlook" until ready
  const [outlookSynced, setOutlookSynced] = useState(false);
  const [syncKey, setSyncKey] = useState(0);
  useEffect(() => {
    if (props.mode !== "draft") return;
    setOutlookSynced(false);
    const timer = setTimeout(() => setOutlookSynced(true), 3000);
    return () => clearTimeout(timer);
  }, [props.mode, syncKey]);

  // Readonly mode state
  const [emailData, setEmailData] = useState<EmailMessage | null>(null);
  const [inboxData, setInboxData] = useState<CheckInboxResponse | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingInbox, setLoadingInbox] = useState(false);

  const emailId = props.mode === "readonly" ? props.emailId : null;

  useEffect(() => {
    if (!emailId) return;
    let cancelled = false;

    // Fetch email body + attachments
    const fetchEmail = async () => {
      setLoadingEmail(true);
      try {
        const data = await api.getEmailWithAttachments(emailId);
        if (!cancelled) setEmailData(data);
      } catch {
        // leave emailData null
      } finally {
        if (!cancelled) setLoadingEmail(false);
      }
    };

    // Check inbox in parallel
    const checkInbox = async () => {
      setLoadingInbox(true);
      try {
        const data = await apiClient.get<CheckInboxResponse>(
          `/documents/email/check_inbox/${encodeURIComponent(emailId)}`
        );
        if (!cancelled) setInboxData(data);
      } catch {
        if (!cancelled) setInboxData({ inInbox: false });
      } finally {
        if (!cancelled) setLoadingInbox(false);
      }
    };

    fetchEmail();
    checkInbox();
    return () => {
      cancelled = true;
    };
  }, [emailId, api, apiClient]);

  // Derive display values based on mode
  let subject = "";
  let body = "";
  let htmlBody = "";
  let fromEmail: string | undefined;
  let toEmails: string[] | undefined;
  let ccEmails: string[] | undefined;
  let dateStr: string | undefined;
  let attachments: (EmailAttachment & { downloadUrl?: string; attachmentId?: string })[] = [];

  if (props.mode === "draft") {
    const data = props.action.data;
    subject = props.draftSubject || (data.replyToEmailSubject as string) || (data.subject as string) || "";
    body = (data.messageContent as string) || "";
    toEmails = props.draftTo;
    ccEmails = props.draftCc && props.draftCc.length > 0 ? props.draftCc : undefined;
    if (props.draftAttachments) {
      attachments = props.draftAttachments.map((a) => ({
        filename: a.filename,
        contentType: a.contentType,
        downloadUrl: a.downloadUrl,
        attachmentId: a.attachmentId,
      })) as (EmailAttachment & { downloadUrl?: string; attachmentId?: string })[];
    }
  } else if (emailData) {
    subject = emailData.subject || "";
    body = emailData.body || "";
    htmlBody = emailData.htmlBody || "";
    fromEmail = emailData.fromEmail;
    toEmails = emailData.toEmails;
    ccEmails = emailData.ccEmails && emailData.ccEmails.length > 0 ? emailData.ccEmails : undefined;
    dateStr = emailData.date
      ? new Date(emailData.date).toLocaleDateString()
      : undefined;
    attachments = emailData.attachments || [];
  }

  const handleSend = async () => {
    if (props.mode !== "draft") return;
    setSending(true);
    try {
      await props.onSend();
    } finally {
      setSending(false);
    }
  };

  const handleAttachmentAdded = async (attachmentId: string) => {
    if (!onAddAttachment) return;
    await onAddAttachment(attachmentId);
    setMissingDismissed(true);
    // Reset the outlook sync timer so the user waits for Outlook to catch up
    setSyncKey((k) => k + 1);
  };

  const handleRemoveAttachmentClick = async (attachmentId: string) => {
    if (!onRemoveAttachment) return;
    setRemovingAttachment(attachmentId);
    try {
      await onRemoveAttachment(attachmentId);
      setSyncKey((k) => k + 1);
    } finally {
      setRemovingAttachment(null);
    }
  };

  const headerTitle =
    props.mode === "draft" ? "Draft Preview" : "Email Preview";

  const isLoading = props.mode === "readonly" && loadingEmail;

  // Existing attachment IDs (to filter from picker)
  const existingAttachmentIds = props.mode === "draft"
    ? ((props.action.data.resolvedAttachments as Array<{ attachmentId: string }>) || []).map((a) => a.attachmentId)
    : [];

  const card = (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "#fff",
        width: "calc(100% - 12px)",
        maxHeight: "80vh",
        overflow: "auto",
        m: "auto",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 0.75,
          bgcolor: "#E3F2FD",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {headerTitle}
        </Typography>
        <IconButton size="small" onClick={onDismiss} sx={{ p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Loading state */}
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
          }}
        >
          <CircularProgress size={14} />
          <Typography variant="body2" color="text.secondary">
            Loading email...
          </Typography>
        </Box>
      )}

      {/* Email content (both modes) */}
      {!isLoading && (subject || body) && (
        <>
          <Box sx={{ px: 1.5, pt: 1 }}>
            {subject && (
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", mb: 0.5 }}
              >
                {subject}
              </Typography>
            )}
            {fromEmail && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                From: {fromEmail}
              </Typography>
            )}
            {toEmails && toEmails.length > 0 && (
              <RecipientLine label="To" emails={toEmails} />
            )}
            {ccEmails && ccEmails.length > 0 && (
              <RecipientLine label="Cc" emails={ccEmails} />
            )}
            {dateStr && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                {dateStr}
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Body */}
          <Box sx={{ px: 1.5, py: 1, maxHeight: 300, overflow: "auto" }}>
            {htmlBody || body ? (
              <div
                style={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.87)" }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    htmlBody ||
                      body?.replace(/\n/g, "<br>") ||
                      "",
                    {
                      ADD_ATTR: ["src", "href", "target", "rel"],
                      ADD_TAGS: [
                        "img", "a", "p", "div", "span", "br",
                        "ul", "ol", "li", "strong", "b", "em", "i", "u",
                        "h1", "h2", "h3", "h4", "h5", "h6",
                        "table", "tr", "td", "th", "tbody", "thead",
                      ],
                      FORBID_TAGS: ["script", "style"],
                    }
                  ),
                }}
              />
            ) : null}
          </Box>

          {/* Attachments + Add button */}
          {(attachments.length > 0 || (props.mode === "draft" && shipmentId)) && (
            <Box sx={{ px: 1.5, pb: 1, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
              {attachments.map((att, i) => {
                // Use attachmentId from the execute response; fall back to resolvedAttachments filename match
                const resolvedList = props.mode === "draft"
                  ? (props.action.data.resolvedAttachments as Array<{ attachmentId: string; filename: string }>) || []
                  : [];
                const attachmentId = att.attachmentId || resolvedList.find((r) => r.filename === att.filename)?.attachmentId;
                const isRemoving = attachmentId ? removingAttachment === attachmentId : false;

                return (
                  <Chip
                    key={attachmentId ?? i}
                    icon={isRemoving ? <CircularProgress size={14} /> : <AttachFileIcon />}
                    label={att.filename || "attachment"}
                    size="small"
                    onClick={() => handleDownloadAttachment(att)}
                    clickable
                    disabled={isRemoving}
                    onDelete={
                      props.mode === "draft" && onRemoveAttachment && attachmentId
                        ? () => handleRemoveAttachmentClick(attachmentId)
                        : undefined
                    }
                    deleteIcon={
                      <CloseIcon sx={{ fontSize: "14px !important" }} />
                    }
                  />
                );
              })}
              {props.mode === "draft" && shipmentId && onAddAttachment && (
                <Chip
                  icon={<AddIcon />}
                  label="Add"
                  size="small"
                  variant="outlined"
                  onClick={() => setPickerOpen(true)}
                  clickable
                  sx={{ borderStyle: "dashed" }}
                />
              )}
            </Box>
          )}

          {/* Missing attachments warning */}
          {showMissing && (
            <Box
              sx={{
                mx: 1.5,
                mb: 1,
                px: 1.5,
                py: 0.75,
                bgcolor: "#FFF3E0",
                borderRadius: 1,
                border: "1px solid #FFB74D",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 18, color: "#E65100", flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: "#E65100", flex: 1 }}>
                Missing attachments: {missingAttachments!.map((m) => m.name).join(", ")}
              </Typography>
              <Button
                size="small"
                onClick={() => setMissingDismissed(true)}
                sx={{ textTransform: "none", fontSize: "0.7rem", color: "#E65100", minWidth: 0, px: 0.5, flexShrink: 0 }}
              >
                Dismiss
              </Button>
            </Box>
          )}

          {/* Show previous messages */}
          {replyToEmailId && !prevEmail && (
            <Box sx={{ px: 1.5, pb: 1 }}>
              {loadingPrev ? (
                <LinearProgress sx={{ my: 1 }} />
              ) : (
                <Button
                  size="small"
                  onClick={handleLoadPrevious}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    color: "#1565C0",
                    px: 0.5,
                  }}
                >
                  Show previous messages
                </Button>
              )}
            </Box>
          )}

          {prevEmail && (
            <>
              <Divider />
              <Box sx={{ px: 1.5, pt: 1 }}>
                {prevEmail.fromEmail && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    From: {prevEmail.fromEmail}
                  </Typography>
                )}
                {prevEmail.date && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    {new Date(prevEmail.date).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              <Box sx={{ px: 1.5, py: 1, maxHeight: 300, overflow: "auto" }}>
                <div
                  style={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.87)" }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      prevEmail.htmlBody ||
                        prevEmail.body?.replace(/\n/g, "<br>") ||
                        "",
                      {
                        ADD_ATTR: ["src", "href", "target", "rel"],
                        ADD_TAGS: [
                          "img", "a", "p", "div", "span", "br",
                          "ul", "ol", "li", "strong", "b", "em", "i", "u",
                          "h1", "h2", "h3", "h4", "h5", "h6",
                          "table", "tr", "td", "th", "tbody", "thead",
                        ],
                        FORBID_TAGS: ["script", "style"],
                      }
                    ),
                  }}
                />
              </Box>
            </>
          )}
        </>
      )}

      {/* Action buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 1.5,
          pb: 1,
          pt: isLoading ? 0 : 1,
        }}
      >
        {props.mode === "draft" && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                outlookSynced ? (
                  <EditIcon sx={{ fontSize: "14px !important" }} />
                ) : (
                  <CircularProgress size={14} />
                )
              }
              onClick={() => actions.openEmail(props.draftMessageId)}
              disabled={!outlookSynced}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                height: 28,
              }}
            >
              Edit in Outlook
            </Button>
            <Tooltip
              title={showMissing ? "Resolve missing attachments before sending" : ""}
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    sending ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <SendIcon sx={{ fontSize: "14px !important" }} />
                    )
                  }
                  onClick={handleSend}
                  disabled={sending || !!showMissing}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    height: 28,
                    bgcolor: "#1565C0",
                    "&:hover": { bgcolor: "#0D47A1" },
                  }}
                >
                  Send
                </Button>
              </span>
            </Tooltip>
          </>
        )}

        {props.mode === "readonly" &&
          !loadingInbox &&
          inboxData?.inInbox &&
          inboxData.messageId && (
            <Button
              variant="contained"
              size="small"
              startIcon={
                <OpenInNewIcon sx={{ fontSize: "14px !important" }} />
              }
              onClick={() => actions.openEmail(inboxData.messageId!)}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                height: 28,
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
              }}
            >
              Show in Outlook
            </Button>
          )}

        {props.mode === "readonly" && loadingInbox && (
          <CircularProgress size={14} />
        )}

        {props.mode === "readonly" &&
          !loadingInbox &&
          inboxData &&
          !inboxData.inInbox && (
            <Chip
              label={`Message in ${inboxData.inboxEmail || "unknown inbox"}`}
              size="small"
              sx={{
                bgcolor: "#E3F2FD",
                color: "#1565C0",
                fontSize: "0.75rem",
              }}
            />
          )}
      </Box>

      {/* Attachment picker dialog */}
      {pickerOpen && shipmentId && onAddAttachment && (
        <EmailPreviewAttachmentPicker
          shipmentId={shipmentId}
          onAttach={handleAttachmentAdded}
          onClose={() => setPickerOpen(false)}
          existingAttachmentIds={existingAttachmentIds}
        />
      )}
    </Box>
  );

  // Always render as full-screen overlay popup
  return (
    <Box
      onClick={onDismiss}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      {card}
    </Box>
  );
};
